import { useRef, useEffect, RefObject } from 'react';

interface SwipeGestureCallbacks {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  onLongPress?: () => void;
  onPullToRefresh?: () => void;
}

interface SwipeGestureOptions {
  threshold?: number;
  velocity?: number;
  longPressDelay?: number;
  pullThreshold?: number;
}

export const useMobileGestures = (
  callbacks: SwipeGestureCallbacks,
  options: SwipeGestureOptions = {}
) => {
  const {
    threshold = 50,
    velocity = 0.3,
    longPressDelay = 500,
    pullThreshold = 80
  } = options;

  const startTouch = useRef<{ x: number; y: number; time: number } | null>(null);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const isPulling = useRef(false);

  const handleTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    startTouch.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };

    // Long press timer
    if (callbacks.onLongPress) {
      longPressTimer.current = setTimeout(() => {
        callbacks.onLongPress?.();
      }, longPressDelay);
    }

    // Check for pull to refresh
    if (callbacks.onPullToRefresh && window.scrollY === 0) {
      isPulling.current = true;
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!startTouch.current) return;

    // Clear long press if moving
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    const touch = e.touches[0];
    const deltaY = touch.clientY - startTouch.current.y;

    // Pull to refresh gesture
    if (isPulling.current && deltaY > pullThreshold && window.scrollY === 0) {
      e.preventDefault();
      // Add visual feedback here if needed
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (!startTouch.current) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - startTouch.current.x;
    const deltaY = touch.clientY - startTouch.current.y;
    const deltaTime = Date.now() - startTouch.current.time;
    
    const velocityX = Math.abs(deltaX) / deltaTime;
    const velocityY = Math.abs(deltaY) / deltaTime;

    // Clear long press timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    // Check for pull to refresh
    if (isPulling.current && deltaY > pullThreshold && window.scrollY === 0) {
      callbacks.onPullToRefresh?.();
    }
    isPulling.current = false;

    // Swipe detection
    if (Math.abs(deltaX) > threshold && velocityX > velocity) {
      if (deltaX > 0) {
        callbacks.onSwipeRight?.();
      } else {
        callbacks.onSwipeLeft?.();
      }
    } else if (Math.abs(deltaY) > threshold && velocityY > velocity) {
      if (deltaY > 0) {
        callbacks.onSwipeDown?.();
      } else {
        callbacks.onSwipeUp?.();
      }
    }

    startTouch.current = null;
  };

  const attachGestures = (element: RefObject<HTMLElement>) => {
    useEffect(() => {
      const el = element.current;
      if (!el) return;

      el.addEventListener('touchstart', handleTouchStart, { passive: false });
      el.addEventListener('touchmove', handleTouchMove, { passive: false });
      el.addEventListener('touchend', handleTouchEnd);

      return () => {
        el.removeEventListener('touchstart', handleTouchStart);
        el.removeEventListener('touchmove', handleTouchMove);
        el.removeEventListener('touchend', handleTouchEnd);
      };
    }, [element]);
  };

  return { attachGestures };
};

// Hook for haptic feedback
export const useHapticFeedback = () => {
  const vibrate = (pattern: number | number[] = 10) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  };

  const lightImpact = () => vibrate(10);
  const mediumImpact = () => vibrate(20);
  const heavyImpact = () => vibrate([10, 50, 10]);

  return {
    vibrate,
    lightImpact,
    mediumImpact,
    heavyImpact
  };
};